class BYOBListItem extends HTMLElement {
  constructor() {    
    super();
    
    this.itemData = JSON.parse(this.querySelector('[data-item-json]').textContent);
    
    this.form = this.querySelector('form');
    this.formId = this.form.getAttribute('id');
    this.CTA = this.querySelector(`button[data-form="${ this.formId }"]`);
    this.quantityInput = this.querySelector(`#${ this.formId }-quantity`);
    
    this.stateName = 'bundle';
  }
  
  connectedCallback() {
    this.CTA.addEventListener('click', this.handleAddToBundleClick.bind(this));
  }
  
  disconnectedCallback() {
    this.CTA.removeEventListener('click', this.handleAddToBundleClick.bind(this));
  }
  
  handleAddToBundleClick(event) {
    event.preventDefault();
    
    const variantId = this.form.id.value;
    const quantity = parseInt(this.quantityInput.value, 10);
    const variant = this.itemData.variants.find(variant => variant.id === variantId);
    const content = {
      image: this.itemData.image,
      title: variant.title ?? 'No name'
    }
    
    const price = variant?.price ?? 0;
    
    document.dispatchEvent(new CustomEvent('bundle:add_to_bundle', {
      detail: {
        variantId,
        sku: variant.sku,
        quantity,
        price,
        content
      }
    }));
    
    this.addToBundleState({ variantId, sku: variant.sku, quantity, price, content });
  }
  
  addToBundleState({ variantId, quantity, price, content, sku }) {
    const bundleObject = { id: variantId, sku, quantity, price, content };
    
    const currentState = JSON.parse(sessionStorage.getItem(this.stateName)) || [];
    const existingObjectIndex = currentState.findIndex(obj => obj.id === bundleObject.id);
    if (existingObjectIndex !== -1) {
      currentState[existingObjectIndex].quantity += bundleObject.quantity;
    } else {
      currentState.push(bundleObject);
    }
    sessionStorage.setItem(this.stateName, JSON.stringify(currentState));
    
    window.dispatchEvent(new Event('bundleStateUpdated'));
  }
}

if (!window.customElements.get('byob-list-item')) {
  window.customElements.define('byob-list-item', BYOBListItem);
}

class BYOBStateBar extends HTMLElement {
  constructor() {
    super();
    this.data = JSON.parse(this.querySelector('[data-bundle-json]').textContent);
    this.bundleObject = this.data.object;
    this.currencyFormat = this.data.currencyFormat;
    this.stateName = 'bundle';
    this.submit = this.querySelector('[data-add-bundle]');
    this.beforePriceEl = this.querySelector('[data-before-price]');
  }
  
  connectedCallback() {
    this.updateView();
    window.addEventListener('bundleStateUpdated', this.updateView.bind(this));
    this.submit.addEventListener('click', this.submitBundleProducts.bind(this));
  }
  
  disconnectedCallback() {
    window.removeEventListener('bundleStateUpdated', this.updateView.bind(this));
    this.submit.removeEventListener('click', this.submitBundleProducts.bind(this));
  }
  
  updateView() {
    const bundleContainer = this.querySelector('[data-bundle-list]');
    this.removeObsoleteDOMItems({ container: bundleContainer });
    this.currentState.forEach(item => {
      const itemInDOM = bundleContainer.querySelector(`[data-id="${item.id}"]`);
      
      if (itemInDOM) {
        const DOMItemQuantityElement = itemInDOM.querySelector('[data-qty]');
        const DOMItemQuantityValue = parseInt(DOMItemQuantityElement?.value ?? 0, 10);
        // Update DOM quantity from state without triggering change events
        if (DOMItemQuantityValue !== item.quantity) {
          DOMItemQuantityElement.value = item.quantity;
        }
      } else {
        // Create and add new item
        const template = document.getElementById('bundle-item-template');
        const newItem = this.createNewItem({ item, template });
        bundleContainer.appendChild(newItem);
      }
    });
    
    this.updateButton();
    this.toggleVisibility();
  }
  
  updateButton() {
    const { readyTranslation, progressTranslation } = this.submit.dataset;
    const { 
      minimum_order_quantity: minimumOrderQuantity,
      discount_in_percentage: discount,
    } = this.bundleObject;
        
    const currentOrderQuantity = this.currentState.length === 1 
      ? this.currentState[0].quantity 
      : this.currentState.reduce((acc, item) => acc + item.quantity, 0);
      
    const currentOrderTotal = this.currentState.length === 1 
      ? this.currentState[0].price * this.currentState[0].quantity
      : this.currentState.reduce((acc, item) => acc + (item.price * item.quantity), 0);
      
    const calculatedOrderTotal = currentOrderTotal - ((discount / 100) * currentOrderTotal);
                
    if (currentOrderQuantity < minimumOrderQuantity) {
      const remaining = minimumOrderQuantity - currentOrderQuantity;
      this.submit.textContent = progressTranslation.replace('{{ count }}', remaining);
      this.submit.disabled = true;
      
      this.beforePriceEl.parentElement.hidden = true;
    } else {
      this.submit.textContent = `${ readyTranslation } · ${ Shopify.formatMoney(calculatedOrderTotal, this.currencyFormat) }`;
      this.submit.disabled = false;
      
      this.beforePriceEl.textContent = `${ Shopify.formatMoney(currentOrderTotal, this.currencyFormat) }`;
      this.beforePriceEl.parentElement.hidden = false;
    }
  }
  
  removeObsoleteDOMItems({ container }) {
    const currentIds = new Set(this.currentState.map(item => item.id));
    [...container.children].forEach(domItem => {
      const itemId = domItem.getAttribute('data-id');
      if (!currentIds.has(itemId)) {
        domItem.remove();
      }
    });
  }
  
  createNewItem({ item, template }) {
    const clone = document.importNode(template.content, true);
    clone.children[0].setAttribute('data-id', item.id);
    clone.children[0].setAttribute('data-sku', item.sku);
    const img = clone.querySelector('.bundle-item-image');
    const title = clone.querySelector('.bundle-item-title');
    const quantity = clone.querySelector('.bundle-item-quantity');
    img.src = item.content.image;
    title.textContent = item.content.title;
    quantity.value = item.quantity;
    return clone;
  }
  
  toggleVisibility() {
    if (this.currentState.length) {
      this.classList.add('visible');
    } else {
      this.classList.remove('visible');
    }
  }
  
  async getCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js`);
    const json = await response.json();
    return json;
  }
  
  async submitBundleProducts() {
    this.submit.disabled = true;
    
    const items = this.currentState;
    const currentCart = await this.getCart();
    
    const itemsAsPermaItems = items.map(item => `${item.id}:${item.quantity}`);
    const cartItemsAsPermaItems = currentCart.items.map(item => `${item.id}:${item.quantity}`);
    const totalCount = [...items, ...currentCart.items].reduce((acc, item) => acc + item.quantity, 0);
    
    const allPermaItems = [...itemsAsPermaItems, ...cartItemsAsPermaItems].filter(Boolean).join(',');
    const permaUrl = `${Shopify.routes.root}cart/${allPermaItems}?discount=${this.bundleObject.discount_code}&storefront=true`;

    
    sessionStorage.setItem(this.stateName, '[]');
    
    // window.location = permaUrl;
    await fetch(permaUrl);
    this.updateCartDrawer({ totalCount });
    
    window.dispatchEvent(new Event('bundleStateUpdated'));
    
    document.dispatchEvent(new CustomEvent('bundle:add_to_cart', {
      detail: { items }
    }));
    
    this.submit.disabled = false;
  }
  
  updateCartDrawer({ totalCount }) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));
    
    const cartDrawer = document.querySelector('cart-drawer');     
    cartDrawer.show();
    
    setTimeout(() => {
      const cartCounts = document.querySelectorAll('cart-count');
      
      cartCounts.forEach(cartCount => {
        cartCount.innerText = totalCount;
        cartCount.style.opacity = 1;
      });
    }, 1000);
  }
  
  get currentState() {
    return JSON.parse(sessionStorage.getItem(this.stateName)) || [];
  }
}

if (!window.customElements.get('byob-state-bar')) {
  window.customElements.define('byob-state-bar', BYOBStateBar);
}

class BYOBBundleItem extends HTMLElement {
  constructor() {
    super();
    
    this.stateName = 'bundle';
    this.variantId = "";
    this.sku = "";
    
    this.removeButton = this.querySelector('[data-remove]');
    this.quantityInput = this.querySelector('[data-qty]');
    this.addQuantityBtn = this.querySelector('[data-add]');
    this.subtractQuantityBtn = this.querySelector('[data-subtract]');
  }
  
  connectedCallback() {
    this.variantId = this.getAttribute('data-id');
    this.sku = this.getAttribute('data-sku');
    
    this.removeButton.addEventListener('click', this.removeItem.bind(this));
    this.quantityInput.addEventListener('blur', this.setQuantity.bind(this));
    this.addQuantityBtn.addEventListener('click', this.addQuantity.bind(this));
    this.subtractQuantityBtn.addEventListener('click', this.subtractQuantity.bind(this));
  }
  
  disconnectedCallback() {
    this.removeButton.removeEventListener('click', this.removeItem.bind(this));
    this.quantityInput.removeEventListener('blur', this.setQuantity.bind(this));
    this.addQuantityBtn.removeEventListener('click', this.addQuantity.bind(this));
    this.subtractQuantityBtn.removeEventListener('click', this.subtractQuantity.bind(this));
  }
  
  removeItem() {
    const updatedState = this.currentState.filter(obj => obj.id !== this.variantId);
    sessionStorage.setItem(this.stateName, JSON.stringify(updatedState));
    
    window.dispatchEvent(new Event('bundleStateUpdated'));
    
    document.dispatchEvent(new CustomEvent('bundle:remove', {
      detail: {
        id: this.sku || this.variantId,
      }
    }));
  }
  
  addQuantity() {
    const newQuantity = Number(this.quantityInput.value) + 1;
    this.quantityInput.value = newQuantity;
    
    this.updateQuantity(newQuantity);
  }
  
  subtractQuantity() {
    const newQuantity = Number(this.quantityInput.value) - 1;
    this.quantityInput.value = newQuantity;
    
    this.updateQuantity(newQuantity);
  }
  
  setQuantity(e) {
    const newQuantity = parseInt(e.target.value, 10);
    
    this.updateQuantity(newQuantity);
  }
  
  updateQuantity(newQuantity) {
    const state = this.currentState;
    
    const existingObjectIndex = state.findIndex(obj => obj.id === this.variantId);
    if (existingObjectIndex !== -1) {
      if (newQuantity < 1) {
        state.splice(existingObjectIndex, 1);
      } else {
        state[existingObjectIndex].quantity = newQuantity;
      }
    }
    sessionStorage.setItem(this.stateName, JSON.stringify(state));
    
    window.dispatchEvent(new Event('bundleStateUpdated'));
  }
  
  get currentState() {
    return JSON.parse(sessionStorage.getItem(this.stateName)) || [];
  }
 }

if (!window.customElements.get('byob-bundle-item')) {
  window.customElements.define('byob-bundle-item', BYOBBundleItem);
}